import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import {
  Home,
  ClipboardList,
  Link2,
  Package,
  FileText,
  Users,
  Archive,
  Building2,
  Settings,
  Sun,
  Moon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  FileInput,
  Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ children, hideHeaderControls = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);

  // Check if user is super user
  useEffect(() => {
    const checkSuperUser = async () => {
      try {
        const response = await fetch(`${API}/admin/is-super-user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setIsSuperUser(data.is_super_user);
        }
      } catch (error) {
        console.error('Error checking super user status:', error);
      }
    };
    if (token) checkSuperUser();
  }, [token]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Auto-collapse on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { 
      icon: Home, 
      label: 'Dashboard', 
      path: '/',
      description: 'Overview & My Tasks'
    },
    { 
      icon: ClipboardList, 
      label: 'Tasks I Assigned', 
      path: '/assigned',
      description: 'Tasks you created'
    },
    { 
      icon: Link2, 
      label: 'Group Assignments', 
      path: '/group-assignments',
      description: 'Bulk task assignments'
    },
    { 
      icon: Package, 
      label: 'Task Bundles', 
      path: '/bundles',
      description: 'Grouped task workflows'
    },
    { 
      icon: FileText, 
      label: 'Task Templates', 
      path: '/templates',
      description: 'Recurring task templates'
    },
    { 
      icon: FileInput, 
      label: 'Intake Forms', 
      path: '/intake-forms',
      description: 'Public submission forms'
    },
    { 
      icon: Users, 
      label: 'Team', 
      path: '/team',
      description: 'Workload & Groups'
    },
    { 
      icon: Archive, 
      label: 'Completed', 
      path: '/completed',
      description: 'Archived tasks'
    },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (location.pathname === path) return true;
    return location.pathname.startsWith(path + '/');
  };

  // On mobile when menu is open, always show expanded sidebar
  const isCollapsed = mobileOpen ? false : collapsed;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo Header */}
      <div className={`flex items-center gap-3 p-4 border-b border-border/50 ${isCollapsed ? 'justify-center' : ''}`}>
        <img 
          src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'} 
          alt="2BeLive" 
          className={`${isCollapsed ? 'h-8 w-8 object-contain' : 'h-10 w-auto'}`}
        />
        {!isCollapsed && (
          <span className="font-heading font-bold text-lg">TaskFlow AI</span>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? item.label : undefined}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'fill-current' : ''}`} />
                {!isCollapsed && (
                  <span className="font-medium text-sm">{item.label}</span>
                )}
              </button>
            );
          })}

          {/* Super User: Company Management */}
          {isSuperUser && (
            <>
              <button
                onClick={() => navigate('/company')}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive('/company') 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? 'Company' : undefined}
                data-testid="nav-company"
              >
                <Building2 className={`w-5 h-5 ${isActive('/company') ? 'fill-current' : ''}`} />
                {!isCollapsed && (
                  <span className="font-medium text-sm">Company</span>
                )}
              </button>
              <button
                onClick={() => navigate('/team-activity')}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive('/team-activity') 
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? 'Team Activity' : undefined}
                data-testid="nav-team-activity"
              >
                <Activity className={`w-5 h-5 ${isActive('/team-activity') ? 'fill-current' : ''}`} />
                {!isCollapsed && (
                  <span className="font-medium text-sm">Team Activity</span>
                )}
              </button>
            </>
          )}
        </nav>
      </ScrollArea>

      {/* Bottom Actions */}
      <div className="border-t border-border/50 p-3 space-y-2">
        {/* Theme Toggle - hidden when header controls are shown */}
        {!hideHeaderControls && (
          <button
            onClick={toggleTheme}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
              text-muted-foreground hover:bg-accent hover:text-foreground
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title={isCollapsed ? (theme === 'dark' ? 'Light Mode' : 'Dark Mode') : undefined}
            data-testid="sidebar-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            {!isCollapsed && (
              <span className="font-medium text-sm">
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </span>
            )}
          </button>
        )}

        {/* User Menu - hidden when header controls are shown */}
        {!hideHeaderControls && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  hover:bg-accent
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                data-testid="sidebar-user-menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!isCollapsed && (
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isCollapsed ? 'center' : 'end'} side="top" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-500" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Collapse Toggle (Desktop only) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-muted-foreground hover:bg-accent hover:text-foreground justify-center"
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border shadow-lg"
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen bg-card border-r z-40
          transition-all duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}
          ${!mobileOpen && collapsed ? 'lg:w-[70px]' : 'lg:w-64'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className={`flex-1 min-h-screen overflow-x-hidden ${collapsed ? 'lg:ml-0' : 'lg:ml-0'}`}>
        {children}
      </main>
    </div>
  );
};

export default Sidebar;
